import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  fetchCartItems,
  AddToCartData,
  addToCart,
  deleteCartItem,
  fetchCart,
  replaceCartItem
} from '@white-label-helper/api-parking-cart';
import useDepositStore from '@white-label-store/deposits';

import type {
  BookingFee,
  SearchProducts,
} from '@white-label-types/parking-booking';
import type { Bundle, CancellationProtection, CartItem, Payable, Total } from '@white-label-types/parking-checkout';
import { PRODUCT_TYPES, ProductType } from '@white-label-configuration/constants';

interface CartBundle extends Bundle {
  hasParking: boolean;
  hasLounges: boolean;
  hasInboundFasttrack: boolean;
  hasOutboundFasttrack: boolean;
  productTypesInBundle: ProductType[];
}

/**
 * Cart Store definition
 *
 *  @returns {StoreDefinition}
 */
export const useCartStore = defineStore('cart', () => {

  /**
   * The current cart token.
   */
  const token = ref<string | null>(null);

  /**
   * The items in the cart.
   */
  const items = ref<CartItem[]>([]);

  /**
   * The bundle in the cart.
   */
  const bundle = ref<CartBundle | null>(null);

  /**
   * The cancellation protection product in the cart.
   */
  const cancellationProtection = ref<null | CancellationProtection>(null);

  const addedExtras = ref<string[]>([]);

  /**
   * Totals object for the current cart.
   * Can be null if the cart is empty.
   */
  const totals = ref<null | Total>(null);

  /**
   * Booking fee for the current cart.
   */
  const bookingFee = ref<null | BookingFee>(null);

  const itemIndex = ref<null | number>(null);
  const itemListName = ref<string>('');
  const lastKnownFilter = ref<string>('');

  /**
   * True if the cart is a multi-basket cart.
   */
  const isMultiBasket = ref<boolean>(false);

  /**
   * The latest item added to the cart.
   */
  const latestItem = ref<CartItem | null>(null);

  /**
   * True if the cart has been updated.
   */
  const cartUpdated = ref<boolean>(false);

  /**
   * True if there is an ongoing cart operation.
   */
  const cartLoading = ref<boolean>(false);

  /**
   * The search product items that are in the cart.
   */
  const searchProductsInCart = ref<SearchProducts[]>([]);

  const readCartItems = computed(() => items.value);
  const cartItemIds = computed(() => items.value.map((item) => item.id));
  const readSearchProductsInCart = computed(() => searchProductsInCart.value);
  const readBundle = computed(() => bundle.value);
  const readToken = computed(() => token.value);
  const readTotals = computed(() => totals.value);
  const readBookingFee = computed(() => bookingFee.value);
  const readAddedExtras = computed(() => addedExtras.value);
  const readCancellationProtection = computed(() => cancellationProtection.value);
  const readItemIndex = computed(() => itemIndex.value);
  const readItemListName = computed(() => itemListName.value);
  const readLastKnownFilter = computed(() => lastKnownFilter.value);
  const readCartUpdated = computed(() => cartUpdated.value);
  const readLatestItem = computed(() => latestItem.value);
  const readCartLoading = computed(() => cartLoading.value);

  function commitStoreItem(itemToStore: CartItem) {
    // Prevent adding duplicates commitStoreItem array.
    const index = items.value.findIndex(item => item.id === itemToStore.id);

    if (index !== -1) {
      Object.assign(items.value[index], itemToStore);
    } else {
      items.value.push(itemToStore);
    }
    latestItem.value = itemToStore;
  }

  function addToSearchProductsInCart(searchProduct: SearchProducts) {
    if (!searchProduct) return;
    const index = searchProductsInCart.value
      .findIndex(product => product?.cart_item_id === searchProduct.cart_item_id);

    if (index !== -1) {
      Object.assign(searchProductsInCart.value[index], searchProduct);
    } else {
      searchProductsInCart.value.push(searchProduct);
    }
  }

  function replaceSearchProductInCart(itemIdToReplace: string, searchProduct: SearchProducts) {
    if (!searchProduct) return;
    const index = searchProductsInCart.value
      .findIndex(product => product?.cart_item_id === itemIdToReplace);

    if (index !== -1) {
      searchProduct.cart_item_id = itemIdToReplace;
      Object.assign(searchProductsInCart.value[index], searchProduct);
    }
  }

  function commitStoreItemList(cartItems: CartItem[]) {
    cartUpdated.value = false;
    items.value = cartItems;
  }

  function updateSearchProductList() {
    if (!token.value) searchProductsInCart.value = [];
    searchProductsInCart.value = searchProductsInCart.value
      .filter((product) => product?.cart_item_id && cartItemIds.value.includes(product.cart_item_id));
  }

  function commitStoreBundle(newBundle: Bundle | null) {
    if (!newBundle) {
      bundle.value = null;
      return;
    }
    bundle.value = {
      ...newBundle,
      hasParking: newBundle.items.some((item) => item.product_code === PRODUCT_TYPES.PARKING),
      hasLounges: newBundle.items.some((item) => item.product_code === PRODUCT_TYPES.LOUNGES),
      hasInboundFasttrack: newBundle.items.some((item) => item.product_code === PRODUCT_TYPES.FASTTRACK && item.inventory_item?.product_option?.code === 'flying_in'),
      hasOutboundFasttrack: newBundle.items.some((item) => item.product_code === PRODUCT_TYPES.FASTTRACK && item.inventory_item?.product_option?.code === 'flying_out'),
      productTypesInBundle: [...new Set(newBundle.items.map((item) => item.product_code))],
    };
  }

  function commitIsMultiBasket(payload: boolean) {
    isMultiBasket.value = payload;
  }

  function commitCartUpdated(payload: boolean) {
    cartUpdated.value = payload;
  }

  function commitStoreTotals(payload: Total) {
    totals.value = payload;
  }

  function commitStoreBookingFee(payload: BookingFee | null) {
    bookingFee.value = payload;
  }

  function storeLatestItem(payload: CartItem) {
    latestItem.value = payload;
  }

  function commitStoreToken(payload: string) {
    token.value = payload;
  }

  function commitClearCart(newToken: string | null = null) {
    items.value = [];
    token.value = newToken;
    cancellationProtection.value = null;
    addedExtras.value = [];
    totals.value = null;
    bundle.value = null;
    bookingFee.value = null;
    searchProductsInCart.value = [];
  }

  function commitStoreCancellationProtection(payload: CancellationProtection | null) {
    cancellationProtection.value = payload;
  }

  function commitAddExtra(id: string) {
    addedExtras.value.push(id);
  }

  function commitRemoveExtra(id: string) {
    addedExtras.value = addedExtras.value.filter((i) => i !== id);
  }

  function commitRemoveBundle() {
    bundle.value = null;
  }

  function commitStoreItemIndex(index: number) {
    itemIndex.value = index;
  }

  function commitStoreItemListName(listName: string) {
    itemListName.value = listName;
  }

  function commitLastKnownFilter(filter: string) {
    lastKnownFilter.value = filter;
  }

  function removeCartItem(id: string) {
    items.value = items.value.filter((item) => item.id !== id);
  }

  function commitCartLoading(isLoading: boolean) {
    cartLoading.value = isLoading;
  }

  async function dispatchFetchCartItems() {
    try {
      if (token.value === null) {
        throw new Error('No token provided');
      }
      const cartData = await fetchCartItems(token.value);
      const deposit = useDepositStore();
      deposit.updateCartDepositTotal({
        ...cartData.payable
      });
      deposit.updateItemDeposit(cartData.items);
      commitStoreTotals(cartData.totals);
      commitStoreBookingFee(cartData.booking_fee);

      if (cartData.items && Array.isArray(cartData.items)) {
        // process all items from the cart in api response
        commitStoreItemList(cartData.items);
        // process only one item from the cart - fallback
      } else if (cartData.items && cartData.items[0]) {
        commitStoreItem(cartData.items[0]);
      } else {
        // in case of no item in the cart
        commitStoreItemList([]);
      }

      updateSearchProductList();

      if (!cartData.items[0].bundle_id) {
        commitStoreCancellationProtection(cartData.items[0].cancellation_protection);
      }
      if (cartData.bundle) {
        commitStoreBundle({
          ...cartData.bundle,
          items: [...cartData.items.filter((item) => item.bundle_id === cartData.bundle?.id)]
        });
      } else {
        commitStoreBundle(null);
      }
    } catch (e) {
      commitClearCart();
      throw e;
    }
  }

  async function dispatchFetchCart() {
    if (token.value === null) {
      throw new Error('No token provided');
    }
    commitCartLoading(true);
    try {
      const { items, totals } = await fetchCart<CartItem, Total, unknown>(token.value);
      commitStoreItemList(items);
      commitStoreTotals(totals);
      updateSearchProductList();
    } catch (e) {
      throw new Error(e instanceof Error ? e.message : String(e));
    } finally {
      commitCartLoading(false);
    }
  }

  async function dispatchAddToCart(data: AddToCartData) {
    const deposit = useDepositStore();
    try {
      const cartData = await addToCart<CartItem, Total, Payable>(data);
      const { items, totals, booking_fee } = cartData;
      deposit.updateCartDepositTotal({
        ...cartData.payable
      });
      deposit.updateItemDeposit(cartData.items);

      const item = items[items.length - 1];
      commitStoreItem(item);
      commitStoreTotals(totals);
      commitCartUpdated(true);

      if (data.searchProduct) {
        addToSearchProductsInCart(data.searchProduct);
      }

      const cancellationProtection = item?.inventory_option?.details?.cancellation_protection;
      const isCancellationProtectionAvailable = cancellationProtection?.is_available;

      if (isCancellationProtectionAvailable) {
        commitStoreBookingFee(booking_fee ?? null);
        commitStoreCancellationProtection(cancellationProtection);
      }
      return { items, totals, booking_fee };
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  }

  async function dispatchReplaceCartItem(data: AddToCartData) {
    try {
      const cartData = await replaceCartItem<CartItem, Total, Payable>(data)
      const { items, totals, booking_fee } = cartData;

      commitStoreItemList(items);
      commitCartUpdated(true);

      if (data.itemIdToReplace && data.searchProduct) {
        replaceSearchProductInCart(data.itemIdToReplace, data.searchProduct);
      }

      return { items, totals, booking_fee };
    } catch(error) {
      console.error('Error replacing item in cart:', error);
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  }

  async function dispatchRemoveFromCart(
    itemId: string,
    options?: { keepToken?: boolean }
  ): Promise<void> {
    const deposit = useDepositStore();
    if (token.value === null) {
      throw new Error('No token provided');
    }

    try {
      const cartData = await deleteCartItem<CartItem, Total, Payable>(token.value, itemId)
      const { items, totals } = cartData;
      removeCartItem(itemId);
      updateSearchProductList();
      deposit.updateCartDepositTotal({
        ...cartData.payable
      });
      deposit.updateItemDeposit(cartData.items);

      if (items.length === 0) {
        if (options?.keepToken && token.value) {
          commitClearCart(token.value);
        } else {
          commitClearCart();
        }
      }

      commitStoreTotals(totals);
    } catch (error) {
      console.error('Error removing item from cart:', error);
      throw error;
    }
  }

  function readOrCreateCartToken(uuid: string): string {
    let cartToken = readToken.value;
    if (!cartToken) {
      cartToken = uuid;
      commitStoreToken(cartToken);
    }
    return cartToken;
  }

  return {
    token,
    items,
    bundle,
    cancellationProtection,
    addedExtras,
    totals,
    bookingFee,
    itemIndex,
    itemListName,
    lastKnownFilter,
    isMultiBasket,
    latestItem,
    cartUpdated,
    cartLoading,
    searchProductsInCart,
    readCartItems,
    cartItemIds,
    readSearchProductsInCart,
    readBundle,
    readToken,
    readTotals,
    readBookingFee,
    readAddedExtras,
    readCancellationProtection,
    readItemIndex,
    readItemListName,
    readLastKnownFilter,
    readCartUpdated,
    readLatestItem,
    readCartLoading,
    commitStoreItem,
    addToSearchProductsInCart,
    commitStoreItemList,
    updateSearchProductList,
    commitStoreBundle,
    commitIsMultiBasket,
    commitCartUpdated,
    commitStoreTotals,
    commitStoreBookingFee,
    storeLatestItem,
    commitStoreToken,
    commitClearCart,
    commitStoreCancellationProtection,
    commitAddExtra,
    commitRemoveExtra,
    commitRemoveBundle,
    commitStoreItemIndex,
    commitStoreItemListName,
    commitLastKnownFilter,
    removeCartItem,
    commitCartLoading,
    dispatchFetchCartItems,
    dispatchFetchCart,
    dispatchAddToCart,
    dispatchRemoveFromCart,
    dispatchReplaceCartItem,
    readOrCreateCartToken,
  }
}, {
  // @ts-expect-error Pinia persist plugin not typed
  persist: [
    {
      storage: process.client ? localStorage : undefined,
      paths: [
        'token',
        'items',
        'bundle',
        'cancellationProtection',
        'addedExtras',
        'totals',
        'bookingFee',
        'itemIndex',
        'itemListName',
        'isMultiBasket',
        'latestItem',
        'cartUpdated',
        'cartLoading',
        'searchProductsInCart',
      ],
    },
    {
      storage: process.client ? sessionStorage : undefined,
      paths: ['lastKnownFilter'],
    },
  ],
});
